import { FormControlError } from "@/components/shared/form-control-error";
import { InputFormControl } from "@/components/shared/input-form-control";
import { RadioCardItem } from "@/components/shared/radio-card-item";
import { Typography } from "@/components/shared/typography";
import { RadioGroup, Skeleton, Textarea, View } from "@/components/ui";
import type { ReportIssueFormReturn } from "@/store/report-issue/forms/useReportIssueForm";
import { useIssueDetailsStore } from "@/store/report-issue/visit-issue/issueDetails";
import { useEffect, useId, useMemo } from "react";
import { Controller } from "react-hook-form";
import { AttachmentsInput, type AttachmentsData } from "./AttachmentsInput";

export function ReportIssueForm({
  form,
  isShuffledOption,
  showAttachments = true,
}: {
  form: ReportIssueFormReturn;
  isShuffledOption?: boolean;
  showAttachments?: boolean;
}) {
  const { data, loading } = useIssueDetailsStore();

  const options = useMemo(() => {
    if (!data) return [];
    if (!isShuffledOption) return data;

    const othersOption = data.find((item) =>
      item.label.toLowerCase().includes("other"),
    );
    const rest = data.filter(
      (item) => !item.label.toLowerCase().includes("other"),
    );

    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }

    return othersOption ? [...rest, othersOption] : rest;
  }, [data, isShuffledOption]);

  useEffect(() => {
    if (options.length > 0) {
      form.setValue("reason", options[0].value);
    }
  }, [options]);

  useEffect(() => {
    if (!showAttachments) {
      form.setValue("complaintPhotos", []);
      form.setValue("complaintVideos", []);
    }
  }, [form, showAttachments]);

  const reasonId = useId();

  const handleAttachmentsChange = (data: AttachmentsData) => {
    form.setValue("complaintPhotos", data.photos);
    form.setValue("complaintVideos", data.videos);
  };

  return (
    <View className="flex flex-col gap-3">
      <Controller
        control={form.control}
        name="reason"
        render={({
          field: { onChange, value, disabled },
          fieldState: { error },
        }) => (
          <View>
            <Typography htmlFor={reasonId} variant="label-sm" className="mb-1">
              Reason*
            </Typography>
            <RadioGroup id={reasonId} value={value} onValueChange={onChange}>
              {loading ? (
                <View className="space-y-2">
                  <Skeleton className="h-6 w-full" />
                </View>
              ) : (
                options.map((option) => (
                  <RadioCardItem
                    key={option.value}
                    label={option.label}
                    value={option.value}
                    selected={option.value === value}
                    disabled={disabled}
                    onChange={onChange}
                  />
                ))
              )}
            </RadioGroup>
            <FormControlError error={error?.message} />
          </View>
        )}
      />

      <InputFormControl
        name="comment"
        label="Comment*"
        control={form.control}
        component={Textarea}
        componentProps={{
          placeholder:
            "Please provide a brief description of the issue you are experiencing",
        }}
      />

      {showAttachments ? (
        <AttachmentsInput onChange={handleAttachmentsChange} />
      ) : null}
    </View>
  );
}
